import classNames from 'classnames';
import type { HTMLMotionProps } from 'framer-motion';
import { motion } from 'framer-motion';
import * as _ from 'lodash-es';
import type { RefObject } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import type { XYCoord } from 'react-dnd';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { useEffectEvent } from 'use-effect-event';
import type { FileDragItem, ThumbnailDragItem } from './CarouselThumbnail';
import CarouselThumbnail, {
  CAROUSEL_ITEM_DRAG_TYPE,
} from './CarouselThumbnail';
import * as styles from './CarouselThumbnailBar.module.scss';
import CarouselThumbnailImage from './CarouselThumbnailImage';
import generateGetImageForDisplay from './generateGetImageForDisplay';
import type { CarouselItem } from './types';
import { isUploadedCarouselItem } from './types';
import useThumbnailBarAutoScroll from './useThumbnailBarAutoScroll';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import ScrollArea from '../../components/ScrollArea';
import { UIActions } from '../../ui/uiSlice';
import addTestId from '../../utils/addTestId';
import Link from '../../utils/router/Link';
import { isEditorCompatibleImage } from '../editorImageMimeTypes';

const applyMovementToIndex = (
  index: number,
  movement: { fromIndex: number; toIndex: number } | null,
) => {
  if (!movement) return index;
  if (movement.fromIndex === index) {
    return movement.toIndex;
  }
  if (movement.fromIndex < index && movement.toIndex >= index) {
    return index - 1;
  }
  if (movement.fromIndex > index && movement.toIndex <= index) {
    return index + 1;
  }
  return index;
};

const applyMovement = <T,>(
  array: T[],
  movement: { fromIndex: number; toIndex: number } | null,
) => {
  if (!movement) return array;
  const next = [...array];
  const [removed] = next.splice(movement.fromIndex, 1);
  if (removed) {
    next.splice(
      movement.toIndex - (movement.toIndex > movement.fromIndex ? 0 : 0),
      0,
      removed,
    );
  }
  return next;
};

const useReorderGroup = <T,>() => {
  // Can be a local variable as onLayoutMeasure is called synchronously on every render
  let orders: { value: T; axis: { min: number; max: number } }[] = [];

  return {
    getOrders: () => orders,
    onLayoutMeasure(value: T, box: { x: { min: number; max: number } }) {
      const nextOrders = [...orders];
      const index = nextOrders.findIndex((o) => o.value === value);
      if (index === -1) {
        nextOrders.push({ value, axis: box.x });
      } else {
        nextOrders[index] = { value, axis: box.x };
      }

      orders = nextOrders.toSorted((a, b) => a.axis.min - b.axis.min);
    },
  };
};

const LAYOUT_TRANSITION_PROPS = {
  layout: 'position',
  transition: { duration: 0.2 },
} as const satisfies Partial<HTMLMotionProps<'div'>>;

const Placeholder = () => (
  <motion.div className={classNames(styles.item)} {...LAYOUT_TRANSITION_PROPS}>
    <CarouselThumbnailImage selected={false} />
  </motion.div>
);

interface CarouselThumbnailBarProps {
  dropTargetRef: RefObject<HTMLDivElement>;
  onRemove?: (image: CarouselItem) => void;
  dragAndDropAllowed: boolean;
  images: CarouselItem[];
  draggingOver: boolean;
  selectedIndex: number;
  onSelectedIndexChange: (index: number) => void;
  onReorder(images: CarouselItem[]): void;
  hoverFileIndex: number | null;
  onAddImages: (files: File[]) => void;
  onHoverFileIndexChange: (index: number | null) => void;
}

const CarouselThumbnailBar = ({
  dropTargetRef,
  onRemove,
  dragAndDropAllowed,
  images: originalImages,
  draggingOver,
  selectedIndex: originalSelectedIndex,
  onSelectedIndexChange,
  onReorder,
  hoverFileIndex,
  onAddImages,
  onHoverFileIndexChange,
}: CarouselThumbnailBarProps) => {
  const [thumbnailImageMovement, setThumbnailImageMovement] = useState<{
    fromIndex: number;
    toIndex: number;
  } | null>(null);

  const { getOrders, onLayoutMeasure } = useReorderGroup();

  const dispatch = useAppDispatch();

  const lastProcessedClientOffsetRef = useRef<XYCoord | null>(null);
  const [{ isOver }, connectDropTarget] = useDrop<
    ThumbnailDragItem | FileDragItem,
    unknown,
    { isOver: boolean }
  >({
    accept: [CAROUSEL_ITEM_DRAG_TYPE, NativeTypes.FILE],
    canDrop: () => dragAndDropAllowed,
    hover(item, monitor) {
      if (!dragAndDropAllowed) return;
      const clientOffset = monitor.getClientOffset();
      if (_.isEqual(lastProcessedClientOffsetRef.current, clientOffset)) {
        return;
      }
      lastProcessedClientOffsetRef.current = clientOffset;

      const x = monitor.getClientOffset()?.x;
      if (typeof x !== 'number') return;

      const orders = getOrders();
      const lastOrder = orders.at(-1);
      if (!lastOrder) {
        return;
      }

      let toIndex = orders.length;
      if (x < (lastOrder.axis.min + lastOrder.axis.max) / 2) {
        orders.some(({ axis }, i) => {
          if (x < (axis.min + axis.max) / 2) {
            toIndex = i;
            return true;
          }
          return false;
        });
      }

      if ('files' in item) {
        if (hoverFileIndex === toIndex) return;
        onHoverFileIndexChange(toIndex);
        return;
      }

      const fromIndex = item.index;
      if (toIndex > fromIndex) {
        toIndex -= 1;
      }

      if (fromIndex >= 0) {
        const nextThumbnailImageMovement =
          toIndex === fromIndex ? null : { fromIndex, toIndex };
        if (!_.isEqual(nextThumbnailImageMovement, thumbnailImageMovement)) {
          setThumbnailImageMovement(nextThumbnailImageMovement);
        }
      }
    },
    collect(monitor) {
      return {
        isOver: monitor.isOver(),
      };
    },
    drop(item) {
      if ('files' in item) {
        if (hoverFileIndex == null) return;
        const files = item.files.filter(isEditorCompatibleImage);
        if (files.length > 0) {
          onAddImages(files);
        }
        if (files.length !== item.files.length) {
          dispatch(
            UIActions.pushToast({
              level: 'warning',
              message: (
                <>
                  Some of those files are not supported for our image carousel.{' '}
                  <Link
                    to="https://help.slab.com/en/articles/3805244-using-images-in-posts#h_767d0bc9d9"
                    newTab
                  >
                    See supported formats for a full list.
                  </Link>
                </>
              ),
            }),
          );
        }
      }
    },
  });

  connectDropTarget(dropTargetRef);

  const onHoverFileIndexChangeEvent = useEffectEvent(onHoverFileIndexChange);
  useEffect(() => {
    if (!isOver) {
      onHoverFileIndexChangeEvent(null);
    }
  }, [isOver]);

  const images = applyMovement(originalImages, thumbnailImageMovement);
  const selectedIndex = applyMovementToIndex(
    originalSelectedIndex,
    thumbnailImageMovement,
  );

  const { containerRef, scrollAreaRef, performScrolling, getTabRefCallback } =
    useThumbnailBarAutoScroll();
  const onSelectedIndexChangeEvent = useEffectEvent(performScrolling);
  useEffect(() => {
    onSelectedIndexChangeEvent(selectedIndex);
  }, [selectedIndex]);

  const getImageForDisplay = generateGetImageForDisplay(images);

  return (
    <ScrollArea
      ref={scrollAreaRef}
      direction="horizontal"
      {...addTestId('imageCarouselThumbnailBar')}
      className={styles.thumbnailBarScrollingContainer}
    >
      <div
        className={classNames(styles.container, {
          [styles.isDraggingOver]: draggingOver,
        })}
        role="tablist"
        aria-label="Slides"
        ref={containerRef}
      >
        {images.map((image, index) => {
          const isSelected = index === selectedIndex;

          return (
            <React.Fragment
              key={
                image.id ??
                (isUploadedCarouselItem(image)
                  ? image.source
                  : image.uploadPreviewSource)
              }
            >
              {index === hoverFileIndex && <Placeholder key="placeholder" />}
              <motion.div
                className={classNames(styles.item, {
                  [styles.isSelected]: isSelected,
                })}
                ref={getTabRefCallback(index)}
                onLayoutMeasure={(box) => onLayoutMeasure?.(image, box)}
                {...LAYOUT_TRANSITION_PROPS}
              >
                <CarouselThumbnail
                  image={getImageForDisplay(image)}
                  index={index}
                  selected={isSelected}
                  onSelectedIndexChange={onSelectedIndexChange}
                  dragControl={{
                    onDragEnd: (didDrop, removed) => {
                      if (!thumbnailImageMovement) return;
                      if (didDrop && !removed) {
                        onReorder(images);
                        onSelectedIndexChange(selectedIndex);
                      }
                      setThumbnailImageMovement(null);
                    },
                    canDrag: () => dragAndDropAllowed,
                    dragItem: {
                      index,
                      isSelected,
                      image,
                      remove: () => onRemove?.(image),
                    },
                  }}
                />
              </motion.div>
            </React.Fragment>
          );
        })}

        {hoverFileIndex === images.length && <Placeholder key="placeholder" />}
      </div>
    </ScrollArea>
  );
};

export default CarouselThumbnailBar;
